import ProgressBar from 'react-bootstrap/ProgressBar';
import SubtleBadge from '@/components/common/SubtleBadge';
import { useAppContext } from '@/Main';
import type { ReactNode } from 'react';
import Icon, { type IconName } from './Icon';
import {
  difficultyTone,
  formatVolume,
  mapIntent,
  mapPurpose
} from '../utils/seoMetrics';
import type { ClusterSeoMetrics } from '../types';

const darkSurfaceStyle = {
  backgroundColor: 'color-mix(in srgb, #000 22%, var(--falcon-card-bg))'
};

const darkIconStyle = {
  width: '2.25rem',
  height: '2.25rem',
  backgroundColor:
    'color-mix(in srgb, var(--falcon-body-color) 8%, transparent)'
};

type MetricStatProps = {
  icon: IconName;
  label: string;
  value: string | number;
  footer?: ReactNode;
  isDark: boolean;
};

function MetricStat({ icon, label, value, footer, isDark }: MetricStatProps) {
  return (
    <div
      className={`rounded-3 px-3 py-3 border${isDark ? '' : ' bg-white'}`}
      style={isDark ? darkSurfaceStyle : undefined}
    >
      <div className="d-flex align-items-center gap-2">
        <div
          className={`rounded-circle d-inline-flex align-items-center justify-content-center text-600 flex-shrink-0${
            isDark ? '' : ' bg-200'
          }`}
          style={
            isDark ? darkIconStyle : { width: '2.25rem', height: '2.25rem' }
          }
        >
          <Icon name={icon} />
        </div>
        <div className="fs--1 text-600 flex-grow-1 min-w-0">{label}</div>
        <div className="fs-2 fw-bold lh-1 text-end flex-shrink-0">{value}</div>
      </div>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}

type SeoMetricsPanelProps = {
  seo: ClusterSeoMetrics | null | undefined;
};

export default function SeoMetricsPanel({ seo }: SeoMetricsPanelProps) {
  const {
    config: { isDark }
  } = useAppContext();

  if (!seo) return null;

  const tone = difficultyTone(seo.difficulty);
  const diff = seo.difficulty == null ? '—' : seo.difficulty;

  const barVariant = {
    easy: 'success',
    fairly_easy: 'primary',
    possible: 'info',
    hard: 'warning',
    very_hard: 'danger',
    impossible: 'secondary'
  }[tone];

  return (
    <div>
      <div className="d-flex flex-column gap-2 mb-3">
        <MetricStat
          icon="bar-chart"
          label="میزان جستجو"
          value={formatVolume(seo.search_volume_miztols)}
          isDark={isDark}
        />
        <MetricStat
          icon="speedometer2"
          label="سختی"
          value={diff}
          isDark={isDark}
          footer={
            seo.difficulty != null ? (
              <>
                <div className="d-flex justify-content-between fs--2 text-600 mb-1">
                  <span>آسان</span>
                  <span className="text-600">ناممکن</span>
                </div>
                <ProgressBar
                  now={seo.difficulty}
                  max={100}
                  variant={barVariant}
                  style={{ height: '0.35rem' }}
                  className="rounded-pill"
                />
              </>
            ) : null
          }
        />
        <MetricStat
          icon="text-paragraph"
          label="میانگین طول کلمات"
          value={seo.len == null ? '—' : seo.len}
          isDark={isDark}
        />
      </div>

      <div className="mb-3">
        <div className="fs--2 text-600 fw-semi-bold mb-2">نیت</div>
        <div className="d-flex flex-wrap gap-1">
          {(seo.intent || []).length ? (
            seo.intent.map(code => {
              const item = mapIntent(code);
              return (
                <SubtleBadge key={code} bg={item.tone} pill className="fs--2">
                  {item.short} · {item.label}
                </SubtleBadge>
              );
            })
          ) : (
            <span className="text-500 fs--2">—</span>
          )}
        </div>
      </div>

      <div>
        <div className="fs--2 text-600 fw-semi-bold mb-2">هدف</div>
        <div className="d-flex flex-wrap gap-1">
          {(seo.purpose || []).length ? (
            seo.purpose.map(item => (
              <SubtleBadge key={item} bg="secondary" pill className="fs--2">
                {mapPurpose(item)}
              </SubtleBadge>
            ))
          ) : (
            <span className="text-500 fs--2">—</span>
          )}
        </div>
      </div>
    </div>
  );
}
